import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Send, Code2, Flame, Trophy, Terminal, Cpu, Braces } from "lucide-react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { useSaveProblem } from "../../hooks/useProblems";

const fetchProblemOfTheDay = async () => {
  const res = await fetch("https://orchestration.trackdsa.in/problems/problemoftheday");
  if (!res.ok) throw new Error("Failed to fetch problems");
  const data = await res.json();
  return data.totalproblem;
};

const Home = () => {
  const profileName = Cookies.get("profileName") 
    ? decodeURIComponent(Cookies.get("profileName")).split(" ")[0] 
    : "Coder";

  const { data: problems = [], isLoading } = useQuery({
    queryKey: ["problemOfTheDay"],
    queryFn: fetchProblemOfTheDay,
  });

  const { mutate: saveProblem, isPending } = useSaveProblem();

  const handleAdd = (problem) => {
    if (!Cookies.get("accessToken")) {
      toast.error("Please login to save problems!");
      return;
    }
    const { __v, _id, ...cleanProblem } = problem;
    saveProblem(cleanProblem);
  };

  // We map the difficulty to specific colors, labels, and icons
  const getDifficultyConfig = (level, index) => {
    // If the API provides a level (1, 2, 3), use it. Otherwise, assume index order (0=Easy, 1=Medium, 2=Hard)
    const normalizedLevel = level ? String(level) : String(index + 1);
    
    switch (normalizedLevel) {
      case "1":
        return { label: "Easy", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", glow: "from-emerald-500/10 to-transparent", Icon: Terminal };
      case "2":
        return { label: "Medium", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", glow: "from-amber-500/10 to-transparent", Icon: Braces };
      case "3":
      default:
        return { label: "Hard", color: "text-red-700", bg: "bg-red-50", border: "border-red-200", glow: "from-red-500/10 to-transparent", Icon: Cpu };
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-10">
      
      {/* Welcome Hero Banner (Fully Mobile Responsive) */}
      <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-white shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-20 -mb-10 w-48 h-48 bg-white opacity-10 rounded-full blur-2xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-5 h-5 text-orange-300" />
            <span className="text-blue-100 font-semibold tracking-wider uppercase text-xs sm:text-sm">Daily Challenge</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 tracking-tight">
            Welcome back, {profileName}!
          </h1>
          <p className="text-blue-100/90 max-w-xl text-base sm:text-lg mb-8 leading-relaxed">
            Consistency is the key to mastering Data Structures and Algorithms. Tackle today's curated problems and keep your streak alive.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link to="/problems" className="w-full sm:w-auto justify-center bg-white text-blue-700 px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-blue-50 transition-colors flex items-center gap-2">
              <Code2 className="w-5 h-5" />
              My Problems
            </Link>
            <Link to="/dsasheet?name=striver" className="w-full sm:w-auto justify-center bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-3 rounded-xl font-semibold backdrop-blur-md transition-colors flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              DSA Sheets
            </Link>
          </div>
        </div>
      </div>

      {/* Problem of the Day Section */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-2">
          <h2 className="text-2xl font-bold text-slate-800">Problem of the Day</h2>
          <span className="text-sm font-medium text-slate-500 bg-slate-100 px-3 py-1 rounded-full w-fit">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </span>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white border border-slate-200 rounded-2xl h-[280px] p-6 flex flex-col justify-between">
                <div className="flex justify-between">
                  <div className="w-12 h-12 bg-slate-200 rounded-xl" />
                  <div className="w-16 h-6 bg-slate-200 rounded-full" />
                </div>
                <div className="space-y-3">
                  <div className="h-6 bg-slate-200 rounded w-3/4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </div>
                <div className="flex gap-2">
                  <div className="h-6 bg-slate-200 rounded w-16" />
                  <div className="h-6 bg-slate-200 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((problem, index) => {
              const config = getDifficultyConfig(problem.difficulty, index);
              const Icon = config.Icon;
              
              return (
                <div 
                  key={problem._id || index} 
                  className="group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden relative"
                >
                  {/* Subtle Gradient Glow in top-right corner */}
                  <div className={`absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl ${config.glow} rounded-bl-full pointer-events-none`} />

                  {/* Card Body */}
                  <div className="flex-1 p-6 flex flex-col relative z-10">
                    {/* Header: Icon & Badge perfectly aligned */}
                    <div className="flex items-start justify-between mb-6">
                      <div className={`p-3 rounded-xl border ${config.bg} ${config.border}`}>
                        <Icon className={`w-6 h-6 ${config.color}`} />
                      </div>
                      <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full border ${config.bg} ${config.color} ${config.border}`}>
                        {config.label}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-slate-800 mb-4 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                      {problem.title}
                    </h3>
                    
                    {/* Topics */}
                    <div className="flex flex-wrap gap-2 mt-auto">
                      {problem.topic.slice(0, 3).map((val) => (
                        <span 
                          key={val} 
                          className="px-2 py-1 text-[11px] font-semibold text-slate-600 bg-slate-100 rounded-md border border-slate-200 uppercase tracking-wider"
                        >
                          {val}
                        </span>
                      ))}
                      {problem.topic.length > 3 && (
                        <span className="px-2 py-1 text-[11px] font-semibold text-slate-500 bg-slate-50 rounded-md border border-slate-200">
                          +{problem.topic.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions (Divided cleanly at the bottom) */}
                  <div className="grid grid-cols-2 divide-x divide-slate-100 border-t border-slate-100 bg-slate-50 relative z-10">
                    <a 
                      href={problem.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 py-3.5 text-sm font-semibold text-slate-600 hover:text-blue-600 hover:bg-slate-100 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </a>
                    <button 
                      onClick={() => handleAdd(problem)}
                      disabled={isPending}
                      className="flex items-center justify-center gap-2 py-3.5 text-sm font-semibold text-slate-600 hover:text-blue-600 hover:bg-slate-100 disabled:opacity-50 transition-colors"
                    >
                      <Send className="w-4 h-4" />
                      {isPending ? "Adding..." : "Add to List"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;